/* eslint-disable max-lines */
import { textVariant } from '@/config'
import { createStyles } from '@codeleap/styles'
import { useMemo } from 'react'

type ChatMessageStyleProps = {
  isMyMessage: boolean
  noFooter: boolean
  hasAttachments: boolean
  hasImages?: boolean
  isDeleted?: boolean
  isThreadParent?: boolean
  hasAudioAttachment?: boolean
  hasPoll?: boolean
  hasTail?: boolean
}

const baseStyles = createStyles(theme => {
  const headingColor = theme.baseColors.onPrimarySolid50Heading
  const captionColor = theme.baseColors.onPrimarySolid50Caption

  return {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: theme.spacing.value(0.2),
      width: '100%',
      maxWidth: '100%',
    },
    editedText: {
      ...textVariant('c3'),
      color: captionColor,
      marginLeft: 'auto',
    },
    messageContainer: {
      display: 'flex',
      flexDirection: 'row',
      flex: 1,
      gap: theme.spacing.value(1),
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      minWidth: 0,
      width: '100%',
    },
    userName: {
      ...textVariant(['c2', 'semiBold']),
      color: captionColor,
    },
    userNameWithQuotedMessage: {
      marginBottom: theme.spacing.value(0.5),
    },
    messageInnerContainer: {
      display: 'flex',
      flexDirection: 'row',
      position: 'relative',
      flex: 1,
      minWidth: 0,
      width: '100%',
    },
    pollWrapper: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: theme.spacing.value(1),
      maxWidth: '100%',
      width: '100%',
      minWidth: 0,
    },
    messageBubble: {
      backgroundColor: theme.baseColors.primarySolid50,
      padding: theme.spacing.value(1.25),
      borderRadius: theme.radius.regular,
      borderBottomLeftRadius: 0,
      position: 'relative',
      maxWidth: 'min(70%, 600px)',
      minWidth: 0,
      overflow: 'visible',
    },
    mention: {
      fontWeight: 'bold',
      color: theme.baseColors.primarySolid500,
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    messageText: {
      ...textVariant('c1'),
      color: headingColor,
      lineHeight: 1.6,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      whiteSpace: 'pre-wrap',
      width: '100%',
      '& pre': {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        overflowX: 'auto',
        maxWidth: '100%',
        margin: 0,
      },
      '& code': {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      },
      '& .str-chat__message-text-inner, & .str-chat__message-text-inner p, & .str-chat__message-text-inner div': {
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
      },
      '& ul, & ol, & li': {
        listStylePosition: 'inside',
      },
      '& a, & .str-chat__message-url-link': {
        color: theme.baseColors.primarySolid500,
        textDecoration: 'underline',
        fontWeight: 500,
        '&:hover': {
          textDecoration: 'none',
        },
      },
    },
    tailStyle: {
      color: theme.baseColors.primarySolid50,
      position: 'absolute',
      top: 0,
      left: 1,
      width: '100%',
      height: '100%',
    },
    bubbleFooter: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.value(1),
      marginTop: theme.spacing.value(0.5),
      width: '100%',
    },
    forwardedIndicator: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.value(0.5),
      marginBottom: theme.spacing.value(0.5),
    },
    forwardedIcon: {
      width: theme.spacing.value(1.5),
      height: theme.spacing.value(1.5),
      color: captionColor,
    },
    forwardedText: {
      color: captionColor,
    },
    footer: {},
    deletedMessageWrapper: {
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      gap: theme.spacing.value(0.5),
    },
    deletedIcon: {
      width: theme.spacing.value(2),
      height: theme.spacing.value(2),
      color: captionColor,
    },
    deletedMessageText: {
      color: captionColor,
    },
    quotedPreviewTouchable: {
      display: 'block',
      width: '100%',
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      textAlign: 'left',
      marginBottom: theme.spacing.value(0.75),
      transition: 'filter 120ms ease',
      '&:hover': {
        filter: 'brightness(0.95)',
      },
    },
    quotedPreviewWrapper: {
      ...theme.presets.row,
      alignItems: 'stretch',
      backgroundColor: theme.baseColors.primarySolid200,
      borderRadius: theme.radius.small,
      overflow: 'hidden',
      minHeight: theme.spacing.value(5),
    },
    quotedPreviewAccent: {
      width: 6,
      borderTopRightRadius: theme.radius.large,
      borderBottomRightRadius: theme.radius.large,
      backgroundColor: theme.baseColors.primarySolid500,
    },
    quotedPreviewBody: {
      ...theme.presets.column,
      ...theme.presets.flex,
      justifyContent: 'center',
      minWidth: 0,
      padding: theme.spacing.value(1),
      gap: theme.spacing.value(0.25),
    },
    quotedPreviewSender: {
      ...textVariant(['c2', 'semiBold']),
      color: theme.baseColors.primarySolid600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    quotedPreviewText: {
      ...textVariant('c2'),
      color: theme.baseColors.primarySolid600,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
    },
    quotedPreviewLinkText: {
      textDecoration: 'underline',
      color: theme.baseColors.primarySolid500,
    },
    quotedPreviewMentionText: {
      fontWeight: 700,
      color: theme.baseColors.primarySolid500,
    },
    quotedPreviewRow: {
      ...theme.presets.row,
      ...theme.presets.alignCenter,
      gap: theme.spacing.value(0.5),
      minWidth: 0,
    },
    quotedPreviewRowText: {
      WebkitLineClamp: 1,
      flex: 1,
      minWidth: 0,
    },
    quotedPreviewIcon: {
      width: theme.spacing.value(1.5),
      height: theme.spacing.value(1.5),
      color: theme.baseColors.primarySolid600,
      flexShrink: 0,
    },
    quotedPreviewThumbnail: {
      width: theme.spacing.value(6),
      height: theme.spacing.value(6),
      objectFit: 'cover',
      flexShrink: 0,
      margin: theme.spacing.value(0.75),
      borderRadius: theme.radius.small,
      alignSelf: 'center',
    },
    quotedPreviewDeletedText: {
      ...textVariant('c2'),
      fontStyle: 'italic',
      color: captionColor,
    },
    quotedPreviewEditedText: {
      ...textVariant('c3'),
      color: captionColor,
    },
  }
})

const modifiers = createStyles(theme => ({
  mentionMyMessage: {
    color: theme.baseColors.onPrimarySolid500Heading,
  },
  wrapperMyMessage: {
    marginLeft: 'auto',
  },
  myMessageDeletedIcon: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  myMessageEditedText: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  myMessageDeletedMessageText: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  messageInnerContainerMyMessage: {
    flexDirection: 'row-reverse',
    paddingRight: 8,
  },
  messageBubbleMyMessage: {
    backgroundColor: theme.baseColors.primarySolid500,
    borderBottomLeftRadius: theme.radius.regular,
  },
  messageBubbleMyMessageHasTail: {
    borderBottomRightRadius: 0,
  },
  messageInnerContainerNoTailMyMessage: {
    marginRight: 8,
  },
  messageInnerContainerNoTail: {
    marginLeft: 8,
  },
  messageTextMyMessage: {
    color: theme.baseColors.onPrimarySolid500Heading,
    '& a, & .str-chat__message-url-link': {
      color: theme.baseColors.onPrimarySolid500Heading,
      '&:hover': {
        color: theme.baseColors.onPrimarySolid500Heading,
      },
    },
  },
  tailStyleMyMessage: {
    bottom: 0.5,
    left: 'auto',
    right: 1,
    rotate: '90deg',
    color: theme.baseColors.primarySolid500,
  },
  bubbleFooterMyMessage: {
    marginLeft: 'auto',
    marginRight: 0,
  },
  forwardedIndicatorMyMessage: {
    marginLeft: 'auto',
    marginRight: 0,
    justifyContent: 'flex-end',
  },
  forwardedTextMyMessage: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  forwardedIconMyMessage: {
    color: theme.baseColors.onPrimarySolid500Caption,
  },
  pollWrapperMyMessage: {
    marginRight: 'auto',
  },

  // No Footer
  wrapperNoFooter: {
    marginBottom: theme.spacing.value(0.5),
  },
  messageBubbleNoFooter: {
    borderBottomLeftRadius: theme.radius.regular,
    borderBottomRightRadius: theme.radius.regular,
  },
  tailStyleNoFooter: {
    opacity: 0,
  },

  // Has Attachments
  messageBubbleHasAttachments: {
    maxWidth: '100%',
  },
  messageTextHasAttachments: {
    marginTop: theme.spacing.value(0.5),
  },
  messageTextMyMessageHasAttachments: {
    textAlign: 'left',
    marginLeft: 'auto',
    width: '100%',
  },

  // Has Images
  messageBubbleHasImages: {
    padding: theme.spacing.value(1),
  },

  // Has Poll
  messageBubbleHasPoll: {
    overflow: 'hidden',
    maxWidth: 'min(100%, 900px)',
    '@media (max-width: 1105px) and (min-width: 893px)': {
      maxWidth: 'min(100%, 950px)',
    },
  },

  // Deleted
  wrapperDeleted: {
    pointerEvents: 'none',
  },
  messageBubbleDeleted: {
    paddingTop: theme.spacing.value(1),
    paddingBottom: theme.spacing.value(1),
  },
  messageTextDeleted: {
    ...textVariant('c1'),
    marginTop: 0,
  },

  // Thread Parent
  wrapperThreadParent: {
    paddingLeft: theme.spacing.value(3),
    paddingRight: theme.spacing.value(3),
  },
  messageInnerContainerThreadParent: {
    marginLeft: 0,
  },
  messageContainerThreadParent: {
    marginRight: 'auto',
    marginLeft: 0,
  },
  messageBubbleThreadParent: {
    marginRight: 'auto',
    padding: theme.spacing.value(1.5),
    borderRadius: theme.radius.regular,
    borderBottomLeftRadius: theme.radius.regular,
    borderBottomRightRadius: theme.radius.regular,
    marginLeft: 0,
  },
  tailStyleThreadParent: {
    display: 'none',
  },
  bubbleFooterThreadParent: {
    marginRight: 'auto',
  },
  footerThreadParent: {
    marginRight: 'auto',
    marginBottom: theme.spacing.value(1),
    marginLeft: theme.spacing.value(1.5),
    paddingLeft: 0,
  },

  footerMyMessage: {
    paddingRight: theme.spacing.value(1),
  },

  quotedPreviewWrapperMyMessage: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  quotedPreviewAccentMyMessage: {
    backgroundColor: theme.baseColors.primarySolid300,
  },
  quotedPreviewSenderMyMessage: {
    color: theme.baseColors.primarySolid50,
  },
  quotedPreviewTextMyMessage: {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  quotedPreviewLinkTextMyMessage: {
    color: theme.baseColors.primarySolid200,
  },
  quotedPreviewMentionTextMyMessage: {
    fontWeight: 700,
    color: theme.baseColors.primarySolid200,
  },
  quotedPreviewIconMyMessage: {
    color: 'rgba(255, 255, 255, 0.9)',
  },
  quotedPreviewEditedTextMyMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
}))

export type ChatMessageStyles = ReturnType<typeof useChatMessageStyles>['styles']

export const useChatMessageStyles = (props: ChatMessageStyleProps) => {
  const {
    isMyMessage,
    noFooter,
    hasAttachments,
    hasImages,
    isDeleted,
    isThreadParent,
    hasPoll,
    hasTail,
  } = props

  const applyMyMessageStyle = isMyMessage

  const applyThreadParentStyle = isThreadParent

  return useMemo(() => {
    const styles = {
      wrapper: {
        ...baseStyles.wrapper,
        ...(applyMyMessageStyle && modifiers.wrapperMyMessage),
        ...(noFooter && modifiers.wrapperNoFooter),
        ...(isDeleted && modifiers.wrapperDeleted),
        ...(applyThreadParentStyle && modifiers.wrapperThreadParent),
      },
      mention: {
        ...baseStyles.mention,
        ...(applyMyMessageStyle && modifiers.mentionMyMessage),
      },
      editedText: {
        ...baseStyles.editedText,
        ...(applyMyMessageStyle && modifiers.myMessageEditedText),
      },
      deletedMessageWrapper: {
        ...baseStyles.deletedMessageWrapper,
      },
      userName: {
        ...baseStyles.userName,
      },
      userNameWithQuotedMessage: {
        ...baseStyles.userNameWithQuotedMessage,
      },
      messageContainer: {
        ...baseStyles.messageContainer,
        ...(applyThreadParentStyle && modifiers.messageContainerThreadParent),
      },
      messageInnerContainer: {
        ...baseStyles.messageInnerContainer,
        ...(applyMyMessageStyle && modifiers.messageInnerContainerMyMessage),
        ...(!hasTail && !applyMyMessageStyle && modifiers.messageInnerContainerNoTail),
        ...(!hasTail && applyMyMessageStyle && modifiers.messageInnerContainerNoTailMyMessage),
        ...(applyThreadParentStyle && modifiers.messageInnerContainerThreadParent),
      },
      pollWrapper: {
        ...baseStyles.pollWrapper,
        ...(applyMyMessageStyle && modifiers.pollWrapperMyMessage),
      },
      messageBubble: {
        ...baseStyles.messageBubble,
        ...(applyMyMessageStyle && modifiers.messageBubbleMyMessage),
        ...(applyMyMessageStyle && hasTail && modifiers.messageBubbleMyMessageHasTail),
        ...(noFooter && modifiers.messageBubbleNoFooter),
        ...(hasAttachments && modifiers.messageBubbleHasAttachments),
        ...(hasImages && modifiers.messageBubbleHasImages),
        ...(hasPoll && modifiers.messageBubbleHasPoll),
        ...(isDeleted && modifiers.messageBubbleDeleted),
        ...(applyThreadParentStyle && modifiers.messageBubbleThreadParent),
      },
      messageText: {
        ...baseStyles.messageText,
        ...(applyMyMessageStyle && modifiers.messageTextMyMessage),
        ...(hasAttachments && modifiers.messageTextHasAttachments),
        ...(hasAttachments && applyMyMessageStyle && modifiers.messageTextMyMessageHasAttachments),
        ...(isDeleted && modifiers.messageTextDeleted),
      },
      tailStyle: {
        ...baseStyles.tailStyle,
        ...(applyMyMessageStyle && modifiers.tailStyleMyMessage),
        ...(noFooter && modifiers.tailStyleNoFooter),
        ...(applyThreadParentStyle && modifiers.tailStyleThreadParent),
      },
      bubbleFooter: {
        ...baseStyles.bubbleFooter,
        ...(applyMyMessageStyle && modifiers.bubbleFooterMyMessage),
        ...(applyThreadParentStyle && modifiers.bubbleFooterThreadParent),
      },
      forwardedIndicator: {
        ...baseStyles.forwardedIndicator,
        ...(applyMyMessageStyle && modifiers.forwardedIndicatorMyMessage),
      },
      forwardedIcon: {
        ...baseStyles.forwardedIcon,
        ...(applyMyMessageStyle && modifiers.forwardedIconMyMessage),
      },
      forwardedText: {
        ...baseStyles.forwardedText,
        ...(applyMyMessageStyle && modifiers.forwardedTextMyMessage),
      },
      footer: {
        ...baseStyles.footer,
        ...(applyMyMessageStyle && modifiers.footerMyMessage),
        ...(applyThreadParentStyle && modifiers.footerThreadParent),
      },
      deletedIcon: {
        ...baseStyles.deletedIcon,
        ...(applyMyMessageStyle && modifiers.myMessageDeletedIcon),
      },
      deletedMessageText: {
        ...baseStyles.deletedMessageText,
        ...(applyMyMessageStyle && modifiers.myMessageDeletedMessageText),
      },
      quotedPreviewTouchable: {
        ...baseStyles.quotedPreviewTouchable,
      },
      quotedPreviewWrapper: {
        ...baseStyles.quotedPreviewWrapper,
        ...(applyMyMessageStyle && modifiers.quotedPreviewWrapperMyMessage),
      },
      quotedPreviewAccent: {
        ...baseStyles.quotedPreviewAccent,
        ...(applyMyMessageStyle && modifiers.quotedPreviewAccentMyMessage),
      },
      quotedPreviewBody: {
        ...baseStyles.quotedPreviewBody,
      },
      quotedPreviewSender: {
        ...baseStyles.quotedPreviewSender,
        ...(applyMyMessageStyle && modifiers.quotedPreviewSenderMyMessage),
      },
      quotedPreviewText: {
        ...baseStyles.quotedPreviewText,
        ...(applyMyMessageStyle && modifiers.quotedPreviewTextMyMessage),
      },
      quotedPreviewLinkText: {
        ...baseStyles.quotedPreviewLinkText,
        ...(applyMyMessageStyle && modifiers.quotedPreviewLinkTextMyMessage),
      },
      quotedPreviewMentionText: {
        ...baseStyles.quotedPreviewMentionText,
        ...(applyMyMessageStyle && modifiers.quotedPreviewMentionTextMyMessage),
      },
      quotedPreviewRow: {
        ...baseStyles.quotedPreviewRow,
      },
      quotedPreviewRowText: {
        ...baseStyles.quotedPreviewRowText,
      },
      quotedPreviewIcon: {
        ...baseStyles.quotedPreviewIcon,
        ...(applyMyMessageStyle && modifiers.quotedPreviewIconMyMessage),
      },
      quotedPreviewThumbnail: {
        ...baseStyles.quotedPreviewThumbnail,
      },
      quotedPreviewDeletedText: {
        ...baseStyles.quotedPreviewDeletedText,
      },
      quotedPreviewEditedText: {
        ...baseStyles.quotedPreviewEditedText,
        ...(applyMyMessageStyle && modifiers.quotedPreviewEditedTextMyMessage),
      },
    }

    // Get color from the appropriate style based on flags
    const tailColor = applyMyMessageStyle
      ? (modifiers.tailStyleMyMessage as any).color
      : (baseStyles.tailStyle as any).color

    return {
      styles,
      tailProps: {
        style: styles.tailStyle,
        color: tailColor,
      },
    }
  }, [applyMyMessageStyle, applyThreadParentStyle, noFooter, hasAttachments, hasImages, hasPoll, isDeleted, hasTail])
}
